"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getTextToolCopy } from "@/lib/text-tool-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function JsonFormatter() {
  const [inputJson, setInputJson] = useState("");
  const [outputJson, setOutputJson] = useState("");
  const [error, setError] = useState("");
  const [indentSize, setIndentSize] = useState(2);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getTextToolCopy(locale);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "json-formatter") ?? tools[0],
    locale
  );

  const formatJson = () => {
    try {
      const parsed = JSON.parse(inputJson);
      const formatted = JSON.stringify(parsed, null, indentSize);
      setOutputJson(formatted);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.invalidJson);
      setOutputJson("");
    }
  };

  const minifyJson = () => {
    try {
      const parsed = JSON.parse(inputJson);
      const minified = JSON.stringify(parsed);
      setOutputJson(minified);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.invalidJson);
      setOutputJson("");
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(outputJson);
  };

  const handleClear = () => {
    setInputJson("");
    setOutputJson("");
    setError("");
  };

  return (

    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:code-json" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <label className="mb-2 block text-lg font-medium text-positivus-dark">{copy.indentSize}</label>
          <div className="relative">
            <select
              value={indentSize}
              onChange={(e) => setIndentSize(parseInt(e.target.value))}
              className="w-full appearance-none rounded-[15px] border-2 border-positivus-dark/20 bg-white px-5 py-4 text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all cursor-pointer"
            >
              <option value={2}>2 {copy.spaces}</option>
              <option value={4}>4 {copy.spaces}</option>
              <option value={8}>8 {copy.spaces}</option>
            </select>
            <Icon icon="mdi:chevron-down" className="absolute right-4 top-1/2 -translate-y-1/2 h-6 w-6 text-positivus-dark pointer-events-none" />
          </div>
        </div>

        <div className="flex gap-3 pt-9">
          <button
            onClick={formatJson}
            className="rounded-[15px] bg-positivus-dark px-6 py-4 font-bold text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_4px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
          >
            {copy.format}
          </button>
          <button
            onClick={minifyJson}
            className="rounded-[15px] border-2 border-positivus-dark bg-white px-6 py-4 font-bold text-positivus-dark hover:bg-positivus-dark hover:text-white transition-all shadow-[0_4px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
          >
            {copy.minify}
          </button>
          <button
            onClick={handleClear}
            className="rounded-[15px] border-2 border-positivus-dark bg-white px-4 py-4 font-bold text-positivus-dark hover:bg-red-50 hover:text-red-600 hover:border-red-600 transition-all shadow-[0_4px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
            title={copy.clearAll}
          >
            <Icon icon={uiIcons.refresh} className="h-6 w-6" />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-[15px] border-2 border-red-500/20 bg-red-50 px-6 py-4 text-sm text-red-600 flex items-center gap-3">
          <Icon icon="mdi:alert-circle" className="h-6 w-6 shrink-0" />
          <p className="font-medium">{error}</p>
        </div>
      )}

      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <label className="mb-3 block text-lg font-medium text-positivus-dark">{copy.inputJson}</label>
          <textarea
            value={inputJson}
            onChange={(e) => setInputJson(e.target.value)}
            placeholder='{"key": "value"}'
            className="min-h-[500px] w-full rounded-[20px] border-2 border-positivus-dark/20 bg-white p-5 font-mono text-sm text-positivus-dark placeholder:text-gray-400 focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all"
          />
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <label className="text-lg font-medium text-positivus-dark">{copy.outputJson}</label>
            {outputJson && (
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 rounded-[10px] bg-positivus-green/20 px-3 py-1.5 text-sm font-bold text-positivus-dark hover:bg-positivus-green hover:text-white transition-colors"
              >
                <Icon icon={uiIcons.copy} className="h-4 w-4" />
                {ui.copy}
              </button>
            )}
          </div>
          <div className="min-h-[500px] rounded-[20px] border-2 border-positivus-dark bg-gray-50 p-6 overflow-hidden">
            <pre className="overflow-x-auto font-mono text-sm text-positivus-dark h-full">{outputJson}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
